import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Plus, Trash2, Save, Edit2, Scale, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import BilanBilateralTable from './BilanBilateralTable';
import CategorySelect from './CategorySelect';
import DownloadFinancePdfButton from './DownloadFinancePdfButton';
import DateRangeFilter, { applyDateRange, formatDateFR } from './DateRangeFilter';
import SearchBox, { applySearch } from './SearchBox';

const ACTIF_CATEGORIES = [
  'Immobilisations incorporelles',
  'Immobilisations corporelles',
  'Immobilisations financières',
  'Stocks',
  'Créances clients',
  'Autres créances',
  'Disponibilités (Banque / Caisse)',
];

const PASSIF_CATEGORIES = [
  'Capital social',
  'Réserves',
  "Résultat de l'exercice",
  'Provisions',
  'Emprunts bancaires',
  'Dettes fournisseurs',
  'Dettes fiscales & sociales',
  'Autres dettes',
];

const fmt = (n) => (parseFloat(n) || 0).toLocaleString('fr-FR');
const today = () => new Date().toISOString().split('T')[0];
const newDraft = (type) => ({
  id: `draft-${Date.now()}-${Math.random()}`,
  date: today(),
  categorie: type === 'actif' ? ACTIF_CATEGORIES[0] : PASSIF_CATEGORIES[0],
  libelle: '',
  montant: 0,
});

export default function BilanPanel({ onBack }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtre par intervalle de dates
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  // Recherche libellé / numéro facture
  const [search, setSearch] = useState('');

  // Dialog de clôture comptable avec choix d'intervalle
  const [showClotureDialog, setShowClotureDialog] = useState(false);
  const todayIso = new Date().toISOString().slice(0, 10);
  const [clotureFrom, setClotureFrom] = useState(`${new Date().getFullYear()}-01-01`);
  const [clotureTo, setClotureTo] = useState(todayIso);
  const [cloturing, setCloturing] = useState(false);

  // Draft rows (unsaved inputs)
  const [actifDraft, setActifDraft] = useState([newDraft('actif')]);
  const [passifDraft, setPassifDraft] = useState([newDraft('passif')]);

  // Quick-add dialogs (formulaires en haut à droite)
  const [quickActif, setQuickActif] = useState(null); // null | { date, categorie, libelle, montant }
  const [quickPassif, setQuickPassif] = useState(null);
  const [savingQuick, setSavingQuick] = useState(false);

  // Catégories dynamiques actif/passif
  const [customActifCats, setCustomActifCats] = useState([]);
  const [customPassifCats, setCustomPassifCats] = useState([]);
  const [hiddenActifDefaults, setHiddenActifDefaults] = useState([]);
  const [hiddenPassifDefaults, setHiddenPassifDefaults] = useState([]);

  const loadCategories = async () => {
    try {
      const r = await axios.get(`${API}/api/financial/categories`);
      setCustomActifCats(r.data?.actifs || []);
      setCustomPassifCats(r.data?.passifs || []);
      setHiddenActifDefaults(r.data?.hiddenDefaults?.actifs || []);
      setHiddenPassifDefaults(r.data?.hiddenDefaults?.passifs || []);
    } catch { /* silent */ }
  };

  // Edit modal
  const [editing, setEditing] = useState(null); // { id, type, categorie, libelle, montant, date }
  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadBilan = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/financial/bilan`);
      setItems(res.data?.items || []);
    } catch (e) {
      console.error(e);
      toast.error('Erreur chargement du bilan');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBilan();
    loadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredItems = applySearch(applyDateRange(items, dateFrom, dateTo, 'date'), search);
  const actifs = filteredItems.filter((i) => i.type === 'actif');
  const passifs = filteredItems.filter((i) => i.type === 'passif');
  const periodLabel = (dateFrom || dateTo)
    ? `${dateFrom ? formatDateFR(dateFrom) : '—'} → ${dateTo ? formatDateFR(dateTo) : '—'}`
    : '';
  const totalActif =
    actifs.reduce((s, i) => s + (parseFloat(i.montant) || 0), 0) +
    actifDraft.reduce((s, r) => s + (parseFloat(r.montant) || 0), 0);
  const totalPassif =
    passifs.reduce((s, i) => s + (parseFloat(i.montant) || 0), 0) +
    passifDraft.reduce((s, r) => s + (parseFloat(r.montant) || 0), 0);

  // ===== Actif draft handlers =====
  const addActifRow = () => setActifDraft((a) => [...a, newDraft('actif')]);
  const removeActifRow = (id) => setActifDraft((a) => a.filter((r) => r.id !== id));
  const updateActifRow = (id, field, value) =>
    setActifDraft((a) => a.map((r) => (r.id === id ? { ...r, [field]: value } : r)));

  // ===== Quick-add (Dialogs en haut à droite) =====
  const openQuickActif = () => setQuickActif({ date: today(), categorie: ACTIF_CATEGORIES[0], libelle: '', montant: 0 });
  const openQuickPassif = () => setQuickPassif({ date: today(), categorie: PASSIF_CATEGORIES[0], libelle: '', montant: 0 });
  const submitQuick = async (type) => {
    const data = type === 'actif' ? quickActif : quickPassif;
    if (!data?.libelle || !(parseFloat(data.montant) > 0)) {
      toast.error('Libellé et montant requis');
      return;
    }
    setSavingQuick(true);
    try {
      await axios.post(`${API}/api/financial/bilan`, {
        type,
        date: data.date,
        categorie: data.categorie,
        libelle: data.libelle,
        montant: parseFloat(data.montant) || 0,
      });
      toast.success(`Ligne ${type === 'actif' ? 'Actif' : 'Passif'} enregistrée`);
      if (type === 'actif') setQuickActif(null); else setQuickPassif(null);
      await loadBilan();
    } catch {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSavingQuick(false);
    }
  };

  const saveActifDraft = async () => {
    const valid = actifDraft.filter((r) => r.libelle && parseFloat(r.montant) > 0);
    if (valid.length === 0) {
      toast.error('Aucune ligne actif valide à enregistrer');
      return;
    }
    try {
      for (const row of valid) {
        await axios.post(`${API}/api/financial/bilan`, {
          type: 'actif',
          categorie: row.categorie,
          libelle: row.libelle,
          montant: parseFloat(row.montant) || 0,
          date: row.date,
        });
      }
      toast.success(`${valid.length} ligne(s) actif enregistrée(s)`);
      setActifDraft([newDraft('actif')]);
      loadBilan();
    } catch (e) {
      console.error(e);
      toast.error("Erreur à l'enregistrement");
    }
  };

  // ===== Passif draft handlers =====
  const addPassifRow = () => setPassifDraft((a) => [...a, newDraft('passif')]);
  const removePassifRow = (id) => setPassifDraft((a) => a.filter((r) => r.id !== id));
  const updatePassifRow = (id, field, value) =>
    setPassifDraft((a) => a.map((r) => (r.id === id ? { ...r, [field]: value } : r)));

  const savePassifDraft = async () => {
    const valid = passifDraft.filter((r) => r.libelle && parseFloat(r.montant) > 0);
    if (valid.length === 0) {
      toast.error('Aucune ligne passif valide à enregistrer');
      return;
    }
    try {
      for (const row of valid) {
        await axios.post(`${API}/api/financial/bilan`, {
          type: 'passif',
          categorie: row.categorie,
          libelle: row.libelle,
          montant: parseFloat(row.montant) || 0,
          date: row.date,
        });
      }
      toast.success(`${valid.length} ligne(s) passif enregistrée(s)`);
      setPassifDraft([newDraft('passif')]);
      loadBilan();
    } catch (e) {
      console.error(e);
      toast.error("Erreur à l'enregistrement");
    }
  };

  // ===== Edit / Delete =====
  const handleEdit = (entry) => setEditing({ ...entry });

  const submitEdit = async () => {
    if (!editing) return;
    try {
      await axios.put(`${API}/api/financial/bilan/${editing.id}`, {
        categorie: editing.categorie,
        libelle: editing.libelle,
        montant: parseFloat(editing.montant) || 0,
        date: editing.date,
      });
      toast.success('Ligne modifiée');
      setEditing(null);
      loadBilan();
    } catch (e) {
      console.error(e);
      toast.error('Erreur à la modification');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/api/financial/bilan/${deleteTarget.id}`);
      toast.success('Ligne supprimée');
      setDeleteTarget(null);
      loadBilan();
    } catch (e) {
      console.error(e);
      toast.error('Erreur à la suppression');
    }
  };

  // ===== Équilibrage automatique via résultat =====
  const ecart = totalActif - totalPassif;
  const equilibre = Math.abs(ecart) < 0.01;

  const equilibrerBilan = async () => {
    if (equilibre) return;
    const isBenefice = ecart > 0;
    try {
      await axios.post(`${API}/api/financial/bilan`, {
        type: isBenefice ? 'passif' : 'actif',
        categorie: "Résultat de l'exercice",
        libelle: `Résultat de l'exercice (${isBenefice ? 'bénéfice' : 'perte'}) - équilibrage auto`,
        montant: Math.abs(ecart),
        date: today(),
      });
      toast.success(
        `Bilan équilibré : ${isBenefice ? 'bénéfice' : 'perte'} de ${fmt(Math.abs(ecart))} FCFA ajouté${
          isBenefice ? ' au Passif' : " à l'Actif"
        }`
      );
      loadBilan();
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de l'équilibrage");
    }
  };

  // ===== Render row helpers =====
  const renderDraftRow = (row, type, update, remove) => {
    const categories = type === 'actif' ? ACTIF_CATEGORIES : PASSIF_CATEGORIES;
    const borderColor = type === 'actif' ? 'border-blue-200' : 'border-amber-200';
    const bgHover = type === 'actif' ? 'bg-blue-50/30 hover:bg-blue-50' : 'bg-amber-50/30 hover:bg-amber-50';
    const textAccent = type === 'actif' ? 'text-blue-700' : 'text-amber-700';
    return (
      <tr key={`draft-${row.id}`} className={`border-b ${bgHover}`}>
        <td className="px-2 py-1">
          <Input
            type="date"
            value={row.date}
            onChange={(e) => update(row.id, 'date', e.target.value)}
            className={`h-8 ${borderColor}`}
          />
        </td>
        <td className="px-2 py-1">
          <Select value={row.categorie} onValueChange={(v) => update(row.id, 'categorie', v)}>
            <SelectTrigger className={`h-8 ${borderColor}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {categories.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </td>
        <td className="px-2 py-1">
          <Input
            value={row.libelle}
            onChange={(e) => update(row.id, 'libelle', e.target.value)}
            placeholder="Libellé..."
            className={`h-8 ${borderColor}`}
          />
        </td>
        <td className="px-2 py-1">
          <Input
            type="number"
            value={row.montant}
            onChange={(e) => update(row.id, 'montant', parseFloat(e.target.value) || 0)}
            className={`h-8 ${borderColor} text-right font-semibold ${textAccent}`}
            min="0"
            step="0.01"
          />
        </td>
        <td className="px-2 py-1">
          <div className="flex items-center justify-center">
            <Button
              size="sm"
              variant="ghost"
              type="button"
              onClick={() => remove(row.id)}
              className="h-8 w-8 p-0 text-red-500 hover:bg-red-100"
              title="Retirer la ligne"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </td>
      </tr>
    );
  };

  const renderSavedRow = (entry, type) => {
    const hoverColor = type === 'actif' ? 'hover:bg-blue-50/60' : 'hover:bg-amber-50/60';
    const badgeColor = type === 'actif' ? 'bg-blue-500 text-white' : 'bg-amber-500 text-white';
    const amountColor = type === 'actif' ? 'text-blue-700' : 'text-amber-700';
    return (
      <tr key={`saved-${entry.id}`} className={`border-b bg-white ${hoverColor}`}>
        <td className="px-3 py-2 whitespace-nowrap text-sm">
          {new Date(entry.date).toLocaleDateString('fr-FR')}
        </td>
        <td className="px-3 py-2">
          <Badge className={`${badgeColor} text-xs`}>{entry.categorie}</Badge>
        </td>
        <td className="px-3 py-2 font-medium text-gray-800 text-sm">{entry.libelle}</td>
        <td className={`px-3 py-2 text-right font-bold whitespace-nowrap ${amountColor}`}>
          {fmt(entry.montant)} FCFA
        </td>
        <td className="px-2 py-2">
          <div className="flex items-center justify-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              type="button"
              className="h-8 w-8 p-0 text-blue-600 hover:bg-blue-100"
              onClick={() => handleEdit(entry)}
              data-testid={`edit-bilan-${entry.id}`}
              title="Modifier"
            >
              <Edit2 className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              type="button"
              className="h-8 w-8 p-0 text-red-600 hover:bg-red-100"
              onClick={() => setDeleteTarget(entry)}
              data-testid={`delete-bilan-${entry.id}`}
              title="Supprimer"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="space-y-6" data-testid="bilan-panel">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={onBack} className="gap-1" data-testid="bilan-back-btn">
            <ArrowLeft className="h-4 w-4" /> Retour
          </Button>
          <DownloadFinancePdfButton
            type="bilan"
            dateFin={today()}
            testId="bilan-admin-download-pdf"
          />
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Scale className="h-6 w-6 text-indigo-600" /> Bilan Comptable
          </h2>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={() => setShowClotureDialog(true)}
            className="bg-purple-600 hover:bg-purple-700 gap-2"
            data-testid="cloture-mois-btn"
            title="Calcule le résultat sur un intervalle et l'inscrit au Bilan"
          >
            <Scale className="h-4 w-4" /> Clôturer le mois
          </Button>
          <Button onClick={openQuickActif} className="bg-blue-600 hover:bg-blue-700 gap-2" data-testid="quick-add-actif-btn">
            <Plus className="h-4 w-4" /> Actif
          </Button>
          <Button onClick={openQuickPassif} className="bg-orange-600 hover:bg-orange-700 gap-2" data-testid="quick-add-passif-btn">
            <Plus className="h-4 w-4" /> Passif
          </Button>
        </div>
      </div>

      {/* Filtre par intervalle de dates + recherche */}
      <div className="flex items-center justify-end gap-2 flex-wrap">
        <SearchBox value={search} onChange={setSearch} color="indigo" testIdPrefix="bilan-search" />
        <DateRangeFilter
          dateFrom={dateFrom}
          dateTo={dateTo}
          onChange={({ dateFrom: f, dateTo: t }) => { setDateFrom(f); setDateTo(t); }}
          color="indigo"
          testIdPrefix="bilan-range"
        />
      </div>

      {/* Résumé */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card
          onClick={() => window.location.assign('/bilan/actif')}
          className="border-l-4 border-l-blue-500 bg-blue-50 cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all"
          data-testid="card-total-actif"
        >
          <CardContent className="p-4">
            <p className="text-xs text-blue-700 font-semibold uppercase">Total Actif →</p>
            <p className="text-2xl font-bold text-blue-800" data-testid="total-actif">
              {fmt(totalActif)} FCFA
            </p>
            <p className="text-xs text-gray-600 mt-1">{actifs.length} ligne(s) enregistrée(s)</p>
          </CardContent>
        </Card>
        <Card
          onClick={() => window.location.assign('/bilan/passif')}
          className="border-l-4 border-l-amber-500 bg-amber-50 cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all"
          data-testid="card-total-passif"
        >
          <CardContent className="p-4">
            <p className="text-xs text-amber-700 font-semibold uppercase">Total Passif →</p>
            <p className="text-2xl font-bold text-amber-800" data-testid="total-passif">
              {fmt(totalPassif)} FCFA
            </p>
            <p className="text-xs text-gray-600 mt-1">{passifs.length} ligne(s) enregistrée(s)</p>
          </CardContent>
        </Card>
        <Card
          className={`border-l-4 ${
            equilibre ? 'border-l-green-500 bg-green-50' : 'border-l-red-500 bg-red-50'
          }`}
        >
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <p
                  className={`text-xs font-semibold uppercase ${
                    equilibre ? 'text-green-700' : 'text-red-700'
                  }`}
                >
                  {equilibre ? 'Bilan Équilibré' : 'Écart'}
                </p>
                <p
                  className={`text-2xl font-bold ${
                    equilibre ? 'text-green-800' : 'text-red-800'
                  }`}
                  data-testid="bilan-ecart"
                >
                  {equilibre ? '✓ 0' : (ecart > 0 ? '+' : '') + fmt(ecart)} FCFA
                </p>
                <p className="text-xs text-gray-600 mt-1">Actif − Passif</p>
              </div>
              {!equilibre && (
                <Button
                  size="sm"
                  onClick={equilibrerBilan}
                  className="bg-indigo-600 hover:bg-indigo-700 gap-1 shrink-0"
                  data-testid="equilibrer-bilan-btn"
                  title={
                    ecart > 0
                      ? `Ajoute un résultat (bénéfice) de ${fmt(Math.abs(ecart))} FCFA au Passif`
                      : `Ajoute un résultat (perte) de ${fmt(Math.abs(ecart))} FCFA à l'Actif`
                  }
                >
                  <Scale className="h-3 w-3" /> Équilibrer
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* TABLEAU BILATÉRAL (style Compte de Résultat) */}
      <BilanBilateralTable items={filteredItems} periodLabel={periodLabel} />

      {/* TABLEAU ACTIF */}
      <Card className="border-2 border-blue-200">
        <CardHeader className="bg-blue-50 flex flex-row items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-blue-700 text-lg">
            Tableau Actif ({actifs.length} enregistrée{actifs.length > 1 ? 's' : ''})
          </CardTitle>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={addActifRow}
              className="gap-1"
              data-testid="add-actif-row-btn"
            >
              <Plus className="h-3 w-3" /> Ligne
            </Button>
            <Button
              size="sm"
              onClick={saveActifDraft}
              className="bg-blue-600 hover:bg-blue-700 gap-1"
              data-testid="save-actif-btn"
            >
              <Save className="h-3 w-3" /> Enregistrer
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-blue-100 text-blue-900">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Date</th>
                  <th className="px-3 py-2 text-left font-semibold">Catégorie</th>
                  <th className="px-3 py-2 text-left font-semibold">Libellé</th>
                  <th className="px-3 py-2 text-right font-semibold">Montant (FCFA)</th>
                  <th className="px-3 py-2 w-20 text-center font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {/* Empty state */}
                {!loading && actifs.length === 0 && actifDraft.length === 0 && (
                  <tr>
                    <td colSpan="5" className="text-center text-gray-400 py-6 text-sm">
                      Aucun actif enregistré
                    </td>
                  </tr>
                )}

                {/* Lignes à saisir EN PREMIER */}
                {actifDraft.map((row) => renderDraftRow(row, 'actif', updateActifRow, removeActifRow))}

                {/* Séparateur */}
                {actifs.length > 0 && actifDraft.length > 0 && (
                  <tr className="bg-blue-50/60">
                    <td
                      colSpan="5"
                      className="px-3 py-1 text-[11px] uppercase tracking-wider font-semibold text-blue-700 border-t border-b border-blue-200"
                    >
                      Actifs déjà enregistrés
                    </td>
                  </tr>
                )}

                {/* Lignes enregistrées */}
                {actifs.map((entry) => renderSavedRow(entry, 'actif'))}
              </tbody>
              <tfoot className="bg-blue-100 font-semibold">
                <tr>
                  <td colSpan="3" className="px-3 py-2 text-right text-blue-900">
                    TOTAL ACTIF :
                  </td>
                  <td className="px-3 py-2 text-right text-blue-700 text-base">{fmt(totalActif)} FCFA</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* TABLEAU PASSIF */}
      <Card className="border-2 border-amber-200">
        <CardHeader className="bg-amber-50 flex flex-row items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-amber-700 text-lg">
            Tableau Passif ({passifs.length} enregistré{passifs.length > 1 ? 's' : ''})
          </CardTitle>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={addPassifRow}
              className="gap-1"
              data-testid="add-passif-row-btn"
            >
              <Plus className="h-3 w-3" /> Ligne
            </Button>
            <Button
              size="sm"
              onClick={savePassifDraft}
              className="bg-amber-600 hover:bg-amber-700 gap-1"
              data-testid="save-passif-btn"
            >
              <Save className="h-3 w-3" /> Enregistrer
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-amber-100 text-amber-900">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Date</th>
                  <th className="px-3 py-2 text-left font-semibold">Catégorie</th>
                  <th className="px-3 py-2 text-left font-semibold">Libellé</th>
                  <th className="px-3 py-2 text-right font-semibold">Montant (FCFA)</th>
                  <th className="px-3 py-2 w-20 text-center font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {!loading && passifs.length === 0 && passifDraft.length === 0 && (
                  <tr>
                    <td colSpan="5" className="text-center text-gray-400 py-6 text-sm">
                      Aucun passif enregistré
                    </td>
                  </tr>
                )}

                {passifDraft.map((row) => renderDraftRow(row, 'passif', updatePassifRow, removePassifRow))}

                {passifs.length > 0 && passifDraft.length > 0 && (
                  <tr className="bg-amber-50/60">
                    <td
                      colSpan="5"
                      className="px-3 py-1 text-[11px] uppercase tracking-wider font-semibold text-amber-700 border-t border-b border-amber-200"
                    >
                      Passifs déjà enregistrés
                    </td>
                  </tr>
                )}

                {passifs.map((entry) => renderSavedRow(entry, 'passif'))}
              </tbody>
              <tfoot className="bg-amber-100 font-semibold">
                <tr>
                  <td colSpan="3" className="px-3 py-2 text-right text-amber-900">
                    TOTAL PASSIF :
                  </td>
                  <td className="px-3 py-2 text-right text-amber-700 text-base">{fmt(totalPassif)} FCFA</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Edit Modal */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md" data-testid="bilan-edit-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="h-5 w-5" /> Modifier la ligne ({editing?.type === 'actif' ? 'Actif' : 'Passif'})
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-sm">Catégorie</Label>
                <Select
                  value={editing.categorie}
                  onValueChange={(v) => setEditing((e) => ({ ...e, categorie: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(editing.type === 'actif' ? ACTIF_CATEGORIES : PASSIF_CATEGORIES).map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Libellé</Label>
                <Input
                  value={editing.libelle || ''}
                  onChange={(e) => setEditing((x) => ({ ...x, libelle: e.target.value }))}
                  data-testid="edit-bilan-libelle"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">Montant (FCFA)</Label>
                  <Input
                    type="number"
                    value={editing.montant}
                    onChange={(e) => setEditing((x) => ({ ...x, montant: parseFloat(e.target.value) || 0 }))}
                    data-testid="edit-bilan-montant"
                  />
                </div>
                <div>
                  <Label className="text-sm">Date</Label>
                  <Input
                    type="date"
                    value={editing.date}
                    onChange={(e) => setEditing((x) => ({ ...x, date: e.target.value }))}
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 flex-row">
            <Button variant="outline" onClick={() => setEditing(null)} className="flex-1">
              Annuler
            </Button>
            <Button
              onClick={submitEdit}
              className="flex-1 bg-indigo-600 hover:bg-indigo-700"
              data-testid="save-edit-bilan-btn"
            >
              Sauvegarder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Supprimer cette ligne ?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600 py-2">
            Confirmer la suppression de <strong>{deleteTarget?.libelle}</strong> (
            {fmt(deleteTarget?.montant || 0)} FCFA) ?
          </p>
          <DialogFooter className="gap-2 flex-row">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} className="flex-1">
              Annuler
            </Button>
            <Button
              onClick={confirmDelete}
              className="flex-1 bg-red-600 hover:bg-red-700"
              data-testid="bilan-confirm-delete-btn"
            >
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Quick-Add ACTIF */}
      <Dialog open={!!quickActif} onOpenChange={(o) => { if (!o) setQuickActif(null); }}>
        <DialogContent className="max-w-lg" data-testid="quick-actif-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <Plus className="h-5 w-5" /> Ajouter une ligne ACTIF
            </DialogTitle>
          </DialogHeader>
          {quickActif && (
            <div className="space-y-4 mt-2">
              <div>
                <Label>Date *</Label>
                <Input type="date" value={quickActif.date}
                       onChange={(e) => setQuickActif({ ...quickActif, date: e.target.value })}
                       data-testid="quick-actif-date" />
              </div>
              <div>
                <Label>Catégorie *</Label>
                <CategorySelect
                  value={quickActif.categorie}
                  onChange={(v) => setQuickActif({ ...quickActif, categorie: v })}
                  type="actif"
                  defaultCategories={ACTIF_CATEGORIES}
                  hiddenDefaults={hiddenActifDefaults}
                  customCategories={customActifCats}
                  onCategoriesChange={(nc, nh) => { setCustomActifCats(nc); if (nh !== undefined) setHiddenActifDefaults(nh); }}
                  triggerClassName="border-blue-200"
                  testId="quick-actif-cat"
                />
              </div>
              <div>
                <Label>Libellé *</Label>
                <Input value={quickActif.libelle}
                       onChange={(e) => setQuickActif({ ...quickActif, libelle: e.target.value })}
                       placeholder="Ex. Panneau publicitaire 4x3"
                       data-testid="quick-actif-libelle" />
              </div>
              <div>
                <Label>Montant (FCFA) *</Label>
                <Input type="number" min="0" step="0.01" value={quickActif.montant}
                       onChange={(e) => setQuickActif({ ...quickActif, montant: parseFloat(e.target.value) || 0 })}
                       data-testid="quick-actif-montant" />
              </div>
            </div>
          )}
          <DialogFooter className="pt-4 border-t">
            <Button variant="outline" onClick={() => setQuickActif(null)}>Annuler</Button>
            <Button onClick={() => submitQuick('actif')} disabled={savingQuick}
                    className="bg-blue-600 hover:bg-blue-700" data-testid="quick-actif-save">
              <Save className="h-4 w-4 mr-1" /> {savingQuick ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Quick-Add PASSIF */}
      <Dialog open={!!quickPassif} onOpenChange={(o) => { if (!o) setQuickPassif(null); }}>
        <DialogContent className="max-w-lg" data-testid="quick-passif-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-700">
              <Plus className="h-5 w-5" /> Ajouter une ligne PASSIF
            </DialogTitle>
          </DialogHeader>
          {quickPassif && (
            <div className="space-y-4 mt-2">
              <div>
                <Label>Date *</Label>
                <Input type="date" value={quickPassif.date}
                       onChange={(e) => setQuickPassif({ ...quickPassif, date: e.target.value })}
                       data-testid="quick-passif-date" />
              </div>
              <div>
                <Label>Catégorie *</Label>
                <CategorySelect
                  value={quickPassif.categorie}
                  onChange={(v) => setQuickPassif({ ...quickPassif, categorie: v })}
                  type="passif"
                  defaultCategories={PASSIF_CATEGORIES}
                  hiddenDefaults={hiddenPassifDefaults}
                  customCategories={customPassifCats}
                  onCategoriesChange={(nc, nh) => { setCustomPassifCats(nc); if (nh !== undefined) setHiddenPassifDefaults(nh); }}
                  triggerClassName="border-orange-200"
                  testId="quick-passif-cat"
                />
              </div>
              <div>
                <Label>Libellé *</Label>
                <Input value={quickPassif.libelle}
                       onChange={(e) => setQuickPassif({ ...quickPassif, libelle: e.target.value })}
                       placeholder="Ex. Emprunt BHCI"
                       data-testid="quick-passif-libelle" />
              </div>
              <div>
                <Label>Montant (FCFA) *</Label>
                <Input type="number" min="0" step="0.01" value={quickPassif.montant}
                       onChange={(e) => setQuickPassif({ ...quickPassif, montant: parseFloat(e.target.value) || 0 })}
                       data-testid="quick-passif-montant" />
              </div>
            </div>
          )}
          <DialogFooter className="pt-4 border-t">
            <Button variant="outline" onClick={() => setQuickPassif(null)}>Annuler</Button>
            <Button onClick={() => submitQuick('passif')} disabled={savingQuick}
                    className="bg-orange-600 hover:bg-orange-700" data-testid="quick-passif-save">
              <Save className="h-4 w-4 mr-1" /> {savingQuick ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog : Clôture comptable avec choix d'intervalle */}
      <Dialog open={showClotureDialog} onOpenChange={setShowClotureDialog}>
        <DialogContent className="sm:max-w-md" data-testid="cloture-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scale className="h-5 w-5 text-purple-600" /> Clôture comptable
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-gray-600">
              Sélectionnez l'intervalle sur lequel calculer le résultat net (Produits − Charges).
              Il sera inscrit au Bilan en tant que <strong>"Résultat de l'exercice"</strong>.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Date de début</Label>
                <Input
                  type="date"
                  value={clotureFrom}
                  onChange={(e) => setClotureFrom(e.target.value)}
                  data-testid="cloture-date-from"
                />
              </div>
              <div>
                <Label className="text-xs">Date de fin</Label>
                <Input
                  type="date"
                  value={clotureTo}
                  onChange={(e) => setClotureTo(e.target.value)}
                  data-testid="cloture-date-to"
                />
              </div>
            </div>
            <div className="text-xs text-purple-700 bg-purple-50 border border-purple-200 rounded p-2">
              💡 Astuce : pour une clôture cumulative annuelle, laissez le <strong>1er janvier</strong> au début et la date du jour à la fin.
            </div>
          </div>
          <DialogFooter className="pt-4 border-t">
            <Button variant="outline" onClick={() => setShowClotureDialog(false)} disabled={cloturing}>
              Annuler
            </Button>
            <Button
              onClick={async () => {
                if (!clotureFrom || !clotureTo) {
                  toast.error('Veuillez renseigner les 2 dates.');
                  return;
                }
                if (clotureFrom > clotureTo) {
                  toast.error('La date de début doit précéder la date de fin.');
                  return;
                }
                setCloturing(true);
                try {
                  // Le `mois` reste utile pour identifier la clôture (basé sur date_fin)
                  const mois = clotureTo.slice(0, 7);
                  const r = await axios.post(
                    `${API}/api/rapports-mensuels/cloture-mois?mois=${mois}&date_debut=${clotureFrom}&date_fin=${clotureTo}`
                  );
                  if (r.data?.already_closed) {
                    toast.info(`La clôture ${mois} existe déjà.`);
                  } else {
                    const rn = r.data?.cloture?.resultat_net || 0;
                    toast.success(
                      `Clôture du ${clotureFrom} au ${clotureTo}. Résultat : ${fmt(Math.abs(rn))} FCFA ${rn >= 0 ? '(bénéfice)' : '(perte)'}`
                    );
                    await loadBilan();
                  }
                  setShowClotureDialog(false);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Échec de la clôture');
                } finally {
                  setCloturing(false);
                }
              }}
              disabled={cloturing}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="cloture-confirm-btn"
            >
              <Scale className="h-4 w-4 mr-1" />
              {cloturing ? 'Clôture en cours…' : 'Clôturer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
